import { lazy, Suspense, useCallback } from 'react';

import LayoutDrawer from 'approot/shared/layout/layout-drawer';
import { useGetInteractiveLessonParams } from '../../../activity-builder.hooks';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import {
  PresentDrawerItem,
  UpdateActiveSectionProps,
} from 'approot/activity-builder/present/activity-builder-present.constants';
import {
  ACTIVITY_BUILDER_EVENT_ACTIVITIES_NAV_ITEM,
  getUpdatedStudentSectionDetails,
} from 'approot/activity-builder/present/activity-builder-present-data-tracking.utils';
import { StudentSectionOrSubSectionOverview } from '../../student.constants';
import * as Styled from 'approot/activity-builder/present/menu/present-menu.style';
import StudentPresentMenu from './student-present-menu';
import StudentPresentMenuActivities from './student-present-menu-activities';
import StudentPresentMenuTranslate from './student-present-menu-translate';
import { LESSON_ACTIVITIES } from 'global/constants';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { useGetStudentLessonAssignmentById } from '../../../../student-lesson/hooks/use-get-student-lesson-assignment-by-id';
import { useGetStudentDataParams } from 'approot/students/logic/use-get-student-data-params';
import { ActivityAnswerPrintContextProvider } from 'approot/activity-builder/print/answers/activity-answers-print.context';

const DownloadStudentAssignmentAnswers = lazy(
  () =>
    import('approot/assignments/download/download-student-assignment-answers')
);

type Props = {
  contentId: number;
  isLoggedIn: boolean;
  activeSectionId: string;
  sectionsOverview: StudentSectionOrSubSectionOverview[];
  currentSectionOverview: StudentSectionOrSubSectionOverview | null;
  currentSectionOverviewIndex: number;
  updateActiveSection: (props: UpdateActiveSectionProps) => void;
};

const StudentPresentMenuLesson = ({
  isLoggedIn,
  activeSectionId,
  sectionsOverview,
  currentSectionOverview,
  currentSectionOverviewIndex,
  updateActiveSection,
}: Props) => {
  const {
    contentType,
    version,
    lang: language,
    assignmentId,
    classId,
  } = useGetInteractiveLessonParams();

  const { assignment, assignedActivities, linkedContent } =
    useGetStudentLessonAssignmentById({
      fetchPolicy: 'cache-and-network',
      classId,
      assignmentId,
      language,
    });

  const { drawer, drawerType, setDrawer, isZoomToFit } =
    useActivityBuilderPresentContext();
  const { teacherClassId } = useGetStudentDataParams();

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const handleClose = useCallback(() => {
    setDrawer({
      isActive: true,
      type: PresentDrawerItem.None,
    });
  }, [setDrawer]);

  return (
    <>
      {isVisible &&
        assignedActivities &&
        assignment &&
        version &&
        linkedContent && (
          <Suspense fallback={<div />}>
            <ActivityAnswerPrintContextProvider
              classId={teacherClassId}
              contentType={LESSON_ACTIVITIES}
              contentId={assignment.contentId}
              showAnswer
            >
              <DownloadStudentAssignmentAnswers
                language={language}
                linkedContent={linkedContent}
                version={version}
                contentId={assignment.contentId}
                contentType={LESSON_ACTIVITIES}
                classId={teacherClassId}
                assignmentId={assignmentId}
                isAuthenticated={isLoggedIn}
                activities={assignedActivities}
                handleClose={closeModal}
              />
            </ActivityAnswerPrintContextProvider>
          </Suspense>
        )}

      <Styled.PresentMenuDetailDrawer $isActive={drawer.isDetailActive}>
        <LayoutDrawer
          isSmall
          isActive={drawerType === PresentDrawerItem.Activities}
          noScrollOnContainer
          captureKeypress={false}
          hasUnderlay={false}
          dataEvent={undefined}
          onClose={handleClose}
        >
          {drawerType === PresentDrawerItem.Activities &&
            assignedActivities &&
            assignment &&
            linkedContent && (
              <StudentPresentMenuActivities
                linkedContent={linkedContent}
                contentId={assignment.contentId}
                contentType={contentType}
                version={version}
                language={language}
                data={assignedActivities}
                activeSectionId={activeSectionId}
                isZoomToFit={isZoomToFit}
                onClose={handleClose}
                onActivityClick={sectionId => {
                  const { newSectionOverview, index } =
                    getUpdatedStudentSectionDetails(
                      sectionId,
                      sectionsOverview
                    );
                  updateActiveSection({
                    sectionId,
                    newSectionOverview,
                    index,
                    eventType: ACTIVITY_BUILDER_EVENT_ACTIVITIES_NAV_ITEM,
                  });
                }}
              />
            )}
        </LayoutDrawer>

        <LayoutDrawer
          isSmall
          isActive={drawerType === PresentDrawerItem.Translate}
          noScrollOnContainer
          captureKeypress={false}
          hasUnderlay={false}
          dataEvent={undefined}
          onClose={handleClose}
        >
          {drawerType === PresentDrawerItem.Translate &&
            version &&
            assignedActivities &&
            assignment && (
              <StudentPresentMenuTranslate
                version={version}
                contentType={contentType}
                lang={language}
                availableTranslations={assignedActivities.availableTranslations}
                onClose={handleClose}
              />
            )}
        </LayoutDrawer>
      </Styled.PresentMenuDetailDrawer>
      {drawer.isActive && (
        <StudentPresentMenu
          activeSectionId={activeSectionId}
          currentSectionOverview={currentSectionOverview}
          currentSectionOverviewIndex={currentSectionOverviewIndex}
          sectionsOverview={sectionsOverview}
          isLoggedIn={isLoggedIn}
          handleDownload={openModal}
          handleOpen={type =>
            setDrawer({
              isActive: true,
              type,
            })
          }
        />
      )}
    </>
  );
};

export default StudentPresentMenuLesson;
